<template>
    <section
        id="faq"
        class="faq"
    >

        <div class="container">

            <div class="faq__layout">

                <div class="faq__header">

                    <span class="faq__eyebrow">
                        {{ 'home_faq' | staticContentAlt }}
                    </span>

                    <h2>
                        {{ 'home_faq' | staticTitle }}
                    </h2>

                    <p>
                        {{ 'home_faq' | staticContent }}
                    </p>

                </div>

                <div class="faq__list">

                    <article
                        v-for="(item, index) in questions"
                        :key="item.hash"
                        class="faq-item"
                        :class="{
                            'faq-item--active':
                                activeIndex === index
                        }"
                    >

                        <button
                            type="button"
                            class="faq-item__button"
                            :aria-expanded="
                                activeIndex === index
                                    ? 'true'
                                    : 'false'
                            "
                            @click="toggleItem(index)"
                        >

                            <div class="faq-item__left">

                                <span class="faq-item__number">
                                    0{{ index + 1 }}
                                </span>

                                <h3>
                                    {{ item.hash | staticTitle }}
                                </h3>

                            </div>

                            <span class="faq-item__icon">
                                <i
                                    class="fa-solid"
                                    :class="
                                        activeIndex === index
                                            ? 'fa-minus'
                                            : 'fa-plus'
                                    "
                                ></i>
                            </span>

                        </button>

                        <transition name="faq-collapse">

                            <div
                                v-if="activeIndex === index"
                                class="faq-item__content"
                            >

                                <div class="faq-item__answer">

                                    <vue-marked>
                                        {{ item.hash | staticContent }}
                                    </vue-marked>

                                </div>

                            </div>

                        </transition>

                    </article>

                </div>

            </div>

        </div>

    </section>
</template>

<script>
export default {
    name: 'HomeFaq',

    data() {
        return {
            activeIndex: 0,

            questions: [
                {
                    hash: 'home_faq_1'
                },
                {
                    hash: 'home_faq_2'
                },
                {
                    hash: 'home_faq_3'
                },
                {
                    hash: 'home_faq_4'
                },
                {
                    hash: 'home_faq_5'
                },
                {
                    hash: 'home_faq_6'
                },
                {
                    hash: 'home_faq_7'
                }
            ]
        };
    },

    methods: {
        toggleItem(index) {
            this.activeIndex =
                this.activeIndex === index
                    ? null
                    : index;
        }
    }
};
</script>

<style lang="scss" scoped>
.faq {
    position: relative;

    padding:
        95px 20px
        110px;

    background:
        radial-gradient(
            circle at 15% 20%,
            rgba(233, 81, 29, .045),
            transparent 28%
        ),
        var(--color-1);

    border-top:
        1px solid
        var(--border-1);

    overflow: hidden;
}

.faq::before {
    content: "";

    position: absolute;

    top: -240px;
    right: -220px;

    width: 520px;
    height: 520px;

    border:
        1px solid
        rgba(255, 255, 255, .035);

    border-radius: 50%;

    pointer-events: none;
}

.container {
    position: relative;

    width: 100%;
    max-width: var(--container);

    margin: 0 auto;

    z-index: 2;
}

.faq__layout {
    display: grid;
    grid-template-columns: 1fr;

    gap: 55px;
}

.faq__header {
    max-width: 680px;

    h2 {
        margin:
            0 0
            20px;

        color: var(--text-1);

        font-size:
            clamp(
                38px,
                7vw,
                64px
            );

        font-weight: 700;
        line-height: .95;
        -letter-spacing: -2px;

        text-transform: uppercase;
    }

    p {
        max-width: 560px;

        margin: 0;

        color: var(--text-2);

        font-size: 14px;
        line-height: 1.8;
    }
}

.faq__eyebrow {
    display: inline-flex;
    align-items: center;

    gap: 12px;

    margin-bottom: 18px;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 10px;
    font-weight: 600;
    -letter-spacing: 2.5px;

    text-transform: uppercase;

    &::before {
        content: "";

        width: 28px;
        height: 1px;

        background: var(--color-3);
    }
}

.faq__list {
    border-top:
        1px solid
        var(--border-1);
}

.faq-item {
    position: relative;

    border-bottom:
        1px solid
        var(--border-1);

    transition:
        background .3s ease,
        border-color .3s ease;

    &--active {
        background:
            rgba(233, 81, 29, .025);

        border-bottom-color:
            rgba(233, 81, 29, .22);
    }
}

.faq-item__button {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 20px;

    padding:
        25px 0;

    border: none;

    background: transparent;

    text-align: left;

    cursor: pointer;

    color: inherit;
}

.faq-item__left {
    display: flex;
    align-items: flex-start;

    gap: 18px;

    min-width: 0;

    h3 {
        margin: 0;

        color: var(--text-1);

        font-size:
            clamp(
                16px,
                2vw,
                20px
            );

        font-weight: 500;
        line-height: 1.4;

        transition:
            color .3s ease;
    }
}

.faq-item--active
.faq-item__left h3 {
    color: var(--text-3);
}

.faq-item__number {
    flex-shrink: 0;

    padding-top: 3px;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 9px;
    -letter-spacing: 1.4px;
}

.faq-item__icon {
    flex-shrink: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 36px;
    height: 36px;

    border:
        1px solid
        var(--border-1);

    color: var(--text-3);

    font-size: 12px;

    transition:
        border-color .3s ease,
        background .3s ease,
        transform .3s ease;
}

.faq-item--active
.faq-item__icon {
    border-color:
        rgba(233, 81, 29, .4);

    background:
        rgba(233, 81, 29, .07);

    transform:
        rotate(180deg);
}

.faq-item__content {
    overflow: hidden;
}

.faq-item__answer {
    max-width: 760px;

    padding:
        0 54px
        28px;

    ::v-deep p {
        margin: 0;

        color: var(--text-2);

        font-size: 14px;
        line-height: 1.85;
    }

    ::v-deep p + p {
        margin-top: 14px;
    }

    ::v-deep ul,
    ::v-deep ol {
        margin:
            14px 0
            0;

        padding-left: 20px;

        color: var(--text-2);

        font-size: 14px;
        line-height: 1.8;
    }

    ::v-deep strong {
        color: var(--text-1);
    }

    ::v-deep a {
        color: var(--text-3);

        text-decoration: none;

        border-bottom:
            1px solid
            rgba(233, 81, 29, .3);
    }
}

.faq-collapse-enter-active,
.faq-collapse-leave-active {
    transition:
        opacity .25s ease,
        transform .25s ease;
}

.faq-collapse-enter,
.faq-collapse-leave-to {
    opacity: 0;

    transform:
        translateY(-8px);
}

@media (min-width: 900px) {
    .faq {
        padding:
            115px 40px
            130px;
    }

    .faq__layout {
        grid-template-columns:
            minmax(280px, .75fr)
            minmax(0, 1.25fr);

        gap: 80px;

        align-items: start;
    }

    .faq__header {
        position: sticky;

        top: 130px;
    }

    .faq-item__button {
        padding:
            28px 0;
    }
}

@media (min-width: 1200px) {
    .faq {
        padding:
            130px 60px
            145px;
    }

    .faq__layout {
        gap: 110px;
    }
}
</style>